import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import React, {
    useEffect,
    useState,
    useContext,
    Fragment,
    useRef,
} from "react";
import { useAuth } from "hooks/Auth";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { modal } from "components/HeadlessModal";
import {
    apiDel,
    apiGet,
    apiPost,
    apiPut,
    messageFromException,
} from "helpers/Request";
import { transform as tagTransformer, TagObjectSchema } from "schemas/Tag";
import { useHistory } from "react-router-dom";
import { toast } from "react-hot-toast";
import { AppContext } from "context/App";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import CheckBox from "components/FormElements/CheckBox";
import Toolbar, { ToolbarGroup } from "components/Toolbar/Toolbar";
import ToolbarButton from "components/Toolbar/ToolbarButton";
import { SaveIcon, TrashIcon, SupportIcon } from "@heroicons/react/outline";
import { useContextualHelp } from "hooks/Help";
import { slugify } from "Utils";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import DangerButton from "components/Buttons/DangerButton";
import SecondaryButton from "components/Buttons/SecondaryButton";

export default function TagEditScreen(props) {
    const { uuid } = useParams();
    const formRef = useRef();
    const location = useLocation();
    const auth = useAuth();
    const history = useHistory();
    const contextualHelp = useContextualHelp();

    const [tag, setTag] = useState(
        Object.assign(TagObjectSchema, { status: true })
    );
    const [formValues, setFormValues] = useState(
        Object.assign(TagObjectSchema, { status: true })
    );
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const { setCurrentNav } = useContext(AppContext);

    const onSubmit = async (values) => {
        const data = Object.assign({}, tag, values);

        const resp = await apiGet("/v4/tag", auth.publication, {
            keyword: data.slug,
        });
        const tags = tagTransformer(resp).filter((t) => {
            return t.slug === data.slug && t.uuid !== tag.uuid;
        });

        if (tags.length) {
            modal.warning({
                title: "Duplicate Tags",
                children: (
                    <Fragment>
                        <div className="mb-4">
                            <strong>
                                It looks like this tag might be a duplicate!
                            </strong>{" "}
                            The following tags already exist:
                            <ul className="d-block my-2 list-inside list-disc space-y-0.5">
                                {tags.map((tag) => (
                                    <li key={tag.uuid}>{tag.name}</li>
                                ))}
                            </ul>
                        </div>
                        <div>Do you still want to create this tag?</div>
                    </Fragment>
                ),
                buttons: () => (
                    <Fragment>
                        <DangerButton onClick={() => saveEntry(values)}>
                            Save
                        </DangerButton>
                        <SecondaryButton onClick={modal.dismiss}>
                            Cancel
                        </SecondaryButton>
                    </Fragment>
                ),
            });
        } else {
            saveEntry(values);
        }
    };

    const saveEntry = async (values) => {
        const data = Object.assign({}, tag, values);
        const id = toast.loading("Saving...");

        setSaving(true);

        const apiAction = tag.uuid ? apiPut : apiPost;
        return apiAction(
            "/v4/tag/" + (tag.uuid ? tag.uuid : ""),
            auth.publication,
            data
        )
            .then((resp) => {
                toast.success("Tag saved", { id });
                modal.dismiss();
                const item = tagTransformer(resp);
                if (!tag.uuid) {
                    // was a new item, so redirect
                    history.push("/app/tag/" + item.uuid);
                }
                setTag(item);
                setSaving(false);
                setTimeout(() => {
                    setLoading(false);
                }, 0);
            })
            .catch((err) => {
                setSaving(false);
                modal.dismiss();
                toast.dismiss();
                modal.error({
                    title: "Error saving tag",
                    children: messageFromException(err),
                });
            });
    };

    const onDelete = () => {
        modal.warning({
            title: "Delete Tag",
            children: (
                <div>
                    <div className="mb-4">
                        <strong>
                            Are you sure you want to delete this tag?
                        </strong>
                    </div>
                    <div>
                        <strong>Note:</strong> This action cannot be undone.
                    </div>
                </div>
            ),
            buttons: () => (
                <Fragment>
                    <DangerButton onClick={() => deleteEntry(uuid)}>
                        Delete
                    </DangerButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    };

    const deleteEntry = async (uuid) => {
        const id = toast.loading("Deleting...");
        return apiDel(`/v4/tag/${tag.uuid}`, auth.publication)
            .then((resp) => {
                toast.success("Tag deleted", { id });
                modal.dismiss();
                history.push("/app/tag");
            })
            .catch((e) => {
                modal.dismiss();
                toast.dismiss(id);
                modal.error({
                    title: "Error deleting tag",
                    children: messageFromException(e),
                });
            });
    };

    useEffect(() => {
        setLoading(true);
        setTag(TagObjectSchema);
        if (!uuid || uuid === "new") {
            setTimeout(() => {
                setLoading(false);
            }, 0);
            return;
        }

        apiGet("/v4/tag/" + uuid, auth.publication)
            .then((response) => {
                const item = tagTransformer(response);

                setTag(item);
                setTimeout(() => {
                    setLoading(false);
                }, 0);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
                modal.error({
                    title: "Error loading tag",
                    children: messageFromException(err),
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, uuid, auth.publication]);

    useEffect(() => {
        setCurrentNav("tag");
    }, [setCurrentNav]);

    useEffect(() => {
        // fixes a bug where the console notes a component updating another, unmounted
        // component. just delay the update until the next tick
        setTimeout(() => {
            setFormValues(tag);
        });
    }, [tag]);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: formValues,
            });
        });
    }, [loading, formValues]);

    return (
        <MainBlock className="z-0">
            <Formik
                innerRef={formRef}
                initialValues={formValues}
                onSubmit={onSubmit}
                enableReinitialize={false}
            >
                {({ setFieldValue, getFieldProps }) => (
                    <Form>
                        <PromptIfDirty />
                        <div className="mx-auto max-w-2xl space-y-4">
                            <Toolbar>
                                <ToolbarGroup>
                                    <ToolbarButton
                                        icon={<SaveIcon />}
                                        type="submit"
                                        disabled={saving}
                                        blocking={saving}
                                    >
                                        Save
                                    </ToolbarButton>
                                    <ToolbarButton
                                        icon={<TrashIcon />}
                                        onClick={onDelete}
                                        disabled={saving || !tag.uuid}
                                    >
                                        Delete
                                    </ToolbarButton>
                                </ToolbarGroup>
                                <ToolbarGroup className="ml-auto">
                                    <ToolbarButton
                                        icon={<SupportIcon />}
                                        className="text-green-600"
                                        onClick={() => {
                                            contextualHelp.showContextualHelp(
                                                "/tag/edit.html"
                                            );
                                        }}
                                    >
                                        Help
                                    </ToolbarButton>
                                </ToolbarGroup>
                            </Toolbar>
                            {loading ? (
                                <Fragment>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                    <div className="h-10 w-full animate-pulse bg-gray-200"></div>
                                </Fragment>
                            ) : (
                                <div className="space-y-4 px-2 sm:px-0">
                                    <TextInput
                                        label="Name"
                                        name="name"
                                        disabled={saving}
                                        onBlur={(e) => {
                                            if (!getFieldProps("slug").value) {
                                                setFieldValue(
                                                    "slug",
                                                    slugify(e.target.value)
                                                );
                                            }
                                        }}
                                    />
                                    <TextInput
                                        label="Slug"
                                        name="slug"
                                        disabled={saving}
                                        onChange={(e) => {
                                            setFieldValue(
                                                "slug",
                                                slugify(e.target.value)
                                            );
                                        }}
                                    />
                                    <CheckBox
                                        label="Status"
                                        name="status"
                                        helpertext="Deactivate tags to keep them from appearing in search results"
                                    />
                                </div>
                            )}
                        </div>
                    </Form>
                )}
            </Formik>
        </MainBlock>
    );
}
